import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

function normalizeGridView(value, viewport) {
  if (typeof window !== 'undefined' && typeof window.__ukNormalizeProductGridView === 'function') {
    return window.__ukNormalizeProductGridView(value, viewport);
  }

  if (viewport === 'mobile') {
    if (value === 'mobile-single' || value === 'cols-2') return 'mobile-single';
    return 'default';
  }

  if (value === 'zoom-out' || value === 'cols-6') return 'cols-6';
  if (value === 'cols-2') return 'cols-2';
  return 'cols-4';
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#restoreStoredLayout();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const nextValue = normalizeGridView(value, viewport);

    await startViewTransition(() => this.#setLayout(nextValue), ['product-grid']);

    requestIdleCallback(() => {
      sessionStorage.setItem(`product-grid-view-${viewport}`, nextValue);
      this.#syncRadios(nextValue, viewport);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const nextValue = normalizeGridView(value, viewport);
    grid.setAttribute('product-grid-view', nextValue);
  }

  #syncRadios(value, viewport) {
    const name = viewport === 'desktop' ? 'grid' : 'grid-mobile';
    this.querySelectorAll(`input.column-options__option-input[name="${name}"]`).forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = input.value === value;
    });
  }

  #restoreStoredLayout() {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const stored = sessionStorage.getItem(`product-grid-view-${viewport}`);
    const value = normalizeGridView(stored, viewport);
    this.#setLayout(value);
    this.#syncRadios(value, viewport);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    const stored = sessionStorage.getItem(`product-grid-view-${viewport}`);
    const value = normalizeGridView(stored, viewport);

    this.#syncRadios(value, viewport);
    this.#setLayout(value);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
